import {
  collection,
  getDocs,
  limit,
  query,
  updateDoc,
  type DocumentData,
  type DocumentReference,
  type QueryDocumentSnapshot,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import {
  DEFAULT_OS_SUPPORT_POLICY,
  DEFAULT_PLATFORM_NOTIFICATION,
  DEFAULT_REVISION_INTERVAL_MONTHS,
  OS_VERSION_PATTERN,
  SEMVER_PATTERN,
  type IInfraSettings,
  type IOsSupportPolicy,
  type IPlatformNotification,
} from "@/features/servers/schemas/servers";

/** Colección de config global ViewMed (blacklist, notificaciones, etc.). */
export const VIEWMED_CONFIG_COLLECTION = "viewmed_config";

function parseRevisionMonths(value: unknown): number {
  if (typeof value === "number" && value >= 1) {
    return Math.min(60, Math.floor(value));
  }
  return DEFAULT_REVISION_INTERVAL_MONTHS;
}

/** Mapa clave → versión objetivo 0.0.0. */
function parseTargetVersions(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof raw !== "string") continue;
    const v = raw.trim();
    if (!v) continue;
    out[key] = v;
  }
  return out;
}

function cleanTargetVersions(
  input: Record<string, string>,
  label: string
): Record<string, string> {
  const cleaned: Record<string, string> = {};
  for (const [key, raw] of Object.entries(input)) {
    const k = key.trim();
    const v = raw.trim();
    if (!k || !v) continue;
    if (!SEMVER_PATTERN.test(v)) {
      throw new Error(
        `Versión inválida para ${label} "${k}": usá formato 0.0.0 (ej. 1.2.0)`
      );
    }
    cleaned[k] = v;
  }
  return cleaned;
}

function parseOsSupportPolicy(value: unknown): IOsSupportPolicy {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { ...DEFAULT_OS_SUPPORT_POLICY };
  }
  const raw = value as Record<string, unknown>;
  const family =
    raw.family === "windows" || raw.family === "other" || raw.family === "ubuntu"
      ? raw.family
      : DEFAULT_OS_SUPPORT_POLICY.family;
  const recommended =
    typeof raw.recommended === "string" && raw.recommended.trim()
      ? raw.recommended.trim()
      : DEFAULT_OS_SUPPORT_POLICY.recommended;
  const minSupported =
    typeof raw.minSupported === "string" && raw.minSupported.trim()
      ? raw.minSupported.trim()
      : DEFAULT_OS_SUPPORT_POLICY.minSupported;
  return { family, recommended, minSupported };
}

function cleanOsSupportPolicy(input: IOsSupportPolicy): IOsSupportPolicy {
  const recommended = input.recommended.trim();
  const minSupported = input.minSupported.trim();
  if (!OS_VERSION_PATTERN.test(recommended)) {
    throw new Error("Versión recomendada: usá formato YY.MM (ej. 26.04)");
  }
  if (!OS_VERSION_PATTERN.test(minSupported)) {
    throw new Error("Versión mínima: usá formato YY.MM (ej. 20.04)");
  }
  const [ry, rm] = recommended.split(".").map(Number);
  const [my, mm] = minSupported.split(".").map(Number);
  const recNum = ry * 100 + rm;
  const minNum = my * 100 + mm;
  if (minNum > recNum) {
    throw new Error(
      "La versión mínima no puede ser mayor que la recomendada"
    );
  }
  return {
    family: input.family,
    recommended,
    minSupported,
  };
}

/**
 * Resuelve el documento único existente en viewmed_config.
 * Nunca crea registros: solo consulta la colección.
 */
async function resolveViewmedConfigDoc(): Promise<
  QueryDocumentSnapshot<DocumentData>
> {
  const snap = await getDocs(
    query(collection(db, VIEWMED_CONFIG_COLLECTION), limit(1))
  );
  const first = snap.docs[0];
  if (!first) {
    throw new Error(
      "No existe el documento de configuración en viewmed_config"
    );
  }
  return first;
}

function parseBlackListEmail(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

function cleanBlackListEmail(input: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    const email = raw.trim().toLowerCase();
    if (!email) continue;
    if (!email.includes("@")) {
      throw new Error(`Email inválido en blacklist: "${raw}"`);
    }
    if (seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}

function parsePlatformNotification(value: unknown): IPlatformNotification {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { ...DEFAULT_PLATFORM_NOTIFICATION };
  }
  const raw = value as Record<string, unknown>;
  return {
    email: typeof raw.email === "boolean" ? raw.email : DEFAULT_PLATFORM_NOTIFICATION.email,
    push: typeof raw.push === "boolean" ? raw.push : DEFAULT_PLATFORM_NOTIFICATION.push,
    ws: typeof raw.ws === "boolean" ? raw.ws : DEFAULT_PLATFORM_NOTIFICATION.ws,
  };
}

function mapInfraSettings(data: DocumentData): IInfraSettings {
  return {
    revisionIntervalMonths: parseRevisionMonths(data.revisionIntervalMonths),
    updatedAt:
      typeof data.revisionIntervalUpdatedAt === "string"
        ? data.revisionIntervalUpdatedAt
        : undefined,
    softwareTargetVersions: parseTargetVersions(data.softwareTargetVersions),
    osSupportPolicy: parseOsSupportPolicy(data.osSupportPolicy),
    blackListEmail: parseBlackListEmail(data.black_list_email),
    notification: parsePlatformNotification(data.notification),
  };
}

/**
 * Lee config de plataforma desde el registro único de viewmed_config.
 */
export async function getInfraSettings(): Promise<IInfraSettings> {
  try {
    const configDoc = await resolveViewmedConfigDoc();
    return mapInfraSettings(configDoc.data());
  } catch {
    return {
      revisionIntervalMonths: DEFAULT_REVISION_INTERVAL_MONTHS,
      softwareTargetVersions: {},
      osSupportPolicy: { ...DEFAULT_OS_SUPPORT_POLICY },
      blackListEmail: [],
      notification: { ...DEFAULT_PLATFORM_NOTIFICATION },
    };
  }
}

export type SaveInfraSettingsInput = {
  revisionIntervalMonths?: number;
  softwareTargetVersions?: Record<string, string>;
  osSupportPolicy?: IOsSupportPolicy;
  blackListEmail?: string[];
  notification?: IPlatformNotification;
};

/**
 * Actualiza campos en el registro único existente (updateDoc, sin crear).
 */
export async function saveInfraSettings(
  input: SaveInfraSettingsInput | number
): Promise<IInfraSettings> {
  const configDoc = await resolveViewmedConfigDoc();
  const ref = configDoc.ref as DocumentReference<DocumentData>;
  const patch: Record<string, unknown> = {};

  const normalized: SaveInfraSettingsInput =
    typeof input === "number" ? { revisionIntervalMonths: input } : input;

  if (normalized.revisionIntervalMonths !== undefined) {
    const months = Math.min(
      60,
      Math.max(1, Math.floor(normalized.revisionIntervalMonths))
    );
    patch.revisionIntervalMonths = months;
    patch.revisionIntervalUpdatedAt = new Date().toISOString();
  }

  if (normalized.softwareTargetVersions !== undefined) {
    patch.softwareTargetVersions = cleanTargetVersions(
      normalized.softwareTargetVersions,
      "software"
    );
  }

  if (normalized.osSupportPolicy !== undefined) {
    patch.osSupportPolicy = cleanOsSupportPolicy(normalized.osSupportPolicy);
  }

  if (normalized.blackListEmail !== undefined) {
    patch.black_list_email = cleanBlackListEmail(normalized.blackListEmail);
  }

  if (normalized.notification !== undefined) {
    patch.notification = {
      email: !!normalized.notification.email,
      push: !!normalized.notification.push,
      ws: !!normalized.notification.ws,
    };
  }

  if (Object.keys(patch).length === 0) {
    return mapInfraSettings(configDoc.data());
  }

  await updateDoc(ref, patch);
  return getInfraSettings();
}

/**
 * True si la fecha está ausente o es anterior al intervalo de revisión.
 */
export function isPastRevisionWindow(
  isoDate: string | undefined,
  intervalMonths: number
): boolean {
  if (!isoDate) return true;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return true;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setMonth(cutoff.getMonth() - intervalMonths);
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  return day < cutoff;
}
